import { IColumnData, IWindowData } from '@/editor/data/model/model';
import { RegionData } from '@/editor/data/region/Region';
import { RoomData } from '@/editor/data/room/Room';
import { WallData } from '@/editor/data/wall/Wall';
import { IPoint2d } from '@/editor/math/IPoint2d';
import type { IFurniture } from '@/editor/data/model/furniture';
import { IDrawSegParams } from '@/pages/development/layout-task/utils/payload';
import { SeparateWallData } from '@/editor/data/separateWall/SeparateWall';
import { ISystemParams, IWallEdge } from '@/editor/types';

/** 自动布局布局类型 */
export enum ELayoutType {
  /** 标准高效型 */
  Standard = 'standard',
  /** ABW 型 */
  ABW = 'abw'
}

/** 自动布局结果中的门窗数据类型 */
interface ILayoutResultDoorWindow {
  center: IPoint2d;
  distance: number;
  height: number;
  id: string;
  relateWallIds: string;
  width: number;
  flip: boolean;
  /** 生成预览图使用 */
  thickness: number;
  side: IWindowData['side'];
  type: IWindowData['type'];
}

export enum ELayoutWallType {
  Wall = 'existWall',
  SolidSeparateWall = 'solidWall',
  GlassSeparateWall = 'glassWall',
  HalfGlassSeparateWall = 'halfGlassWall',
  VirtualSeparateWall = 'virtualWall'
}

interface IAutoLayoutOutSeparateWall {
  end: IPoint2d;
  height: number;
  id: string;
  start: IPoint2d;
  thickness: number;
  type: ELayoutWallType;
}

interface IAutoLayoutOutSeparateWallWithId extends IAutoLayoutOutSeparateWall {
  id: string;
}

interface IAutoLayoutOutRegion {
  regionInfo: RegionData['regionInfo'];
  separateWallDatas: IAutoLayoutOutSeparateWall[];
}

export interface ILayoutResultFurniture {
  center: IPoint2d;
  height: number;
  modelId?: IFurniture['modelId'];
  offGround: number;
  rotation: number;
  width: number;
}

export interface ILayoutUserInput {
  LayoutType: ELayoutType;
  assembledWalls: AssembledWalls;
  mainHallway: MainHallway;
  publicArea: PublicArea;
  nonPublic: NonPublic;
}

interface AssembledWalls {
  thickness: number;
}

interface MainHallway {
  width: number;
}

interface NonPublic {
  nonPublicSwitch: number;
  Rooms: Room[];
  officeArea: OfficeArea;
  recreationArea: RecreationArea;
  discussionArea: DiscussionArea;
}

interface Room {
  quantities: number;
  numbers: number;
  tablesId: string;
  doorNumber: number;
  doorId: string;
}

interface DiscussionArea {
  percentage: number;
  tablesIdList: string[];
  sofasIdList: string;
  greenneryId: string;
}

interface OfficeArea {
  stepNumber: number;
  stepId: string;
  islandSpaceing: number;
  percentage: number;
  tablesId: string[];
  tableWidth: number;
  tableHeight: number;
  CabinetMagnification: number;
  fileCabinetFm: number;
  printerId: string;
  shredder: string;
  greenneryId: string;
}

interface RecreationArea {
  percentage: number;
  tablesIdList: string[];
  sofasIdList: string[];
  coffceMachineId: string;
  greenneryId: string;
}

interface PublicArea {
  publicSwitch: number;
  Rooms: Room[];
  doorId: string;
}

type IAutoLayoutWindowData = IWindowData & { entrance?: boolean };

export interface IFullLayoutSeg {
  start: IPoint2d;
  end: IPoint2d;
}

export interface ILayoutPathSeg {
  start: IPoint2d;
  end: IPoint2d;
  type: ELayoutWallType;
  thickness: number;
}

export interface IAutoLayoutParamsBaseMessage {
  systemParams: ISystemParams;
  roomDatas: RoomData[];
  wallDatas: WallData[];
  pathSegs: ILayoutPathSeg[];
  // separateWallDatas: SeparateWallData[];
  doorWindowDatas: IAutoLayoutWindowData[];
  furnitureDatas: IFurniture[];
  columnDatas: IColumnData[];
  regionDatas: RegionData[];
}

export enum ERegionTypeForPartialLayout {
  StandardOffice = 'standardOffice',
  AbwOffice = 'abwOffice',
  Rest = 'rest',
  Discuss = 'discuss'
}

export interface IPartialLayoutUserInput {
  regionType: ERegionTypeForPartialLayout;
  deskRelativeMessage: {
    width: number;
    height: number;
    stepNumber: number;
    seat?: number;
    islandSpaceing?: number;
  };
  furnitureDatas: {
    isGroup: boolean;
    modelId: string;
    quantities: number;
  }[];
}

interface ILayoutIWallEdge extends Omit<IWallEdge, 'center' | 'clockwise'> {
  center: IPoint2d | null;
  clockwise: boolean | null;
}

export interface IFullLayoutBase {
  systemParams: ISystemParams;
  pathSegs: IFullLayoutSeg[];
  holeSegs: IFullLayoutSeg[][];
  pathInfos: ILayoutIWallEdge[];
  holeInfos: ILayoutIWallEdge[][];
  doorWindowDatas: IAutoLayoutWindowData[];
  doorWindows: IAutoLayoutWindowData[];
  columnDatas: IColumnData[];
  roomData: RoomData;
  wallDatas: WallData[];
  currentFurnitureDatas: IFurniture[];
}

export interface ILayoutBaseMessage {
  systemParams: ISystemParams;
  roomDatas: RoomData[];
  wallDatas: WallData[];
  pathSegs: ILayoutPathSeg[];
  holeSegs: { start: IPoint2d; end: IPoint2d }[][];
  doorWindowDatas: IAutoLayoutWindowData[];
  furnitureDatas: IFurniture[];
  currentFurnitureDatas: IFurniture[];
  columnDatas: IColumnData[];
  regionDatas: RegionData[];
}

export interface IPartialLayoutParams {
  userInput: IPartialLayoutUserInput;
  baseMessage: ILayoutBaseMessage;
}

export interface IPartialLayoutResult {
  error?: unknown;
  previewImageUrl: string;
  furnitureDatas: ILayoutResultFurniture[];
}

export interface IPartialLayoutResultData extends IPartialLayoutParams {
  outPutMessage: IPartialLayoutResult[];
}

export interface ILayoutParams {
  userInput: ILayoutUserInput;
  baseMessage: IFullLayoutBase;
}

export interface IFullLayoutResult {
  error?: unknown;
  previewImageUrl: string;
  doorWindowDatas: ILayoutResultDoorWindow[];
  furnitureDatas: ILayoutResultFurniture[];
  regionDatas: IAutoLayoutOutRegion[];
  separateWallDatas: IAutoLayoutOutSeparateWallWithId[];
}

export interface IFullLayoutResultData extends ILayoutParams {
  outPutMessage: IFullLayoutResult[];
}

export type IAutoRoom = Room;

export enum ELayoutTaskStatus {
  Pending = 'PENDING',
  Failed = 'FAILED',
  Completed = 'COMPLETED'
}

interface ILayoutTaskPending {
  status: ELayoutTaskStatus.Pending;
  taskId: string;
  dataId: string;
}

interface ILayoutTaskFailed {
  status: ELayoutTaskStatus.Failed;
  taskId: string;
  dataId: string;
  errorMessage: string;
  errorCode: string;
}

interface ILayoutTaskCompleted<T> {
  status: ELayoutTaskStatus.Completed;
  taskId: string;
  dataId: string;
  result: T;
}

export type ILayoutTask<T> = ILayoutTaskPending | ILayoutTaskFailed | ILayoutTaskCompleted<T>;

export enum ELayoutTaskResultItemStatus {
  Pending = 'pending',
  Failed = 'failed',
  Completed = 'completed'
}

export interface ILayourTaskList {
  current: number;
  page: number;
  size: number;
  total: number;
  records: ILayoutTaskResultListItem[];
}

export enum ELayoutReportType {
  SUCCESS = 'success',
  ILLEGAL_SEP_WALLS = 'illegalSepWalls',
  UNUSED_REGION = 'unusedRegion',
  ILLEGAL_SEP_WALLS_AND_UNUSED_REGION = 'illegalSepWallsAndUnusedRegion',
  APPLY_FAILED = 'applyFailed'
}

export interface ELayoutReportData {
  alignedData: IDrawSegParams;
  generatedData: IDrawSegParams;
  illegalSepWalls: SeparateWallData[];
  unusedRegions: RegionData[];
  applyIndex: number;
  /** 本次生成抽到的前端最短等待时长，单位 ms。早于该字段上线的记录没有它 */
  minWait?: number;
}

export interface ILayoutTaskResultListItem {
  duration: number;
  createUserBizId: string;
  userName: string;
  email: string;
  status: ELayoutTaskResultItemStatus;
  createTime: string;
  id: string;
  type: 'ROOM' | 'REGION';
  reportType: ELayoutReportType;
}

export interface ILayoutTaskResultListItemDetails extends ILayoutTaskResultListItem {
  payload: unknown;
  response: unknown;
  reportData: unknown;
}
